/**
 * Admin · Enlaces de reserva por tour.
 *
 * Permite definir, para cada tour del catálogo, dónde se completa la reserva:
 * checkout propio (`own`) o página real del proveedor (`external`:
 * GetYourGuide, Civitatis, LadyGuía…). Maya usa EXCLUSIVAMENTE estos registros.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Save, Trash2, TestTube2 } from "lucide-react";
import { toast } from "sonner";

import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { unifiedCatalogQuery } from "@/lib/catalog-query";
import { makeTourSlug } from "@/lib/slug";
import { isSafeExternalUrl, type BookingType, type TourBookingLink } from "@/lib/booking-links";
import {
  deleteBookingLink,
  listBookingLinksAdmin,
  testBookingUrl,
  upsertBookingLink,
} from "@/lib/booking-links.functions";

export const Route = createFileRoute("/_authenticated/admin/tour-booking-links")({
  component: TourBookingLinksPage,
  head: () => ({
    meta: [
      { title: "Enlaces de reserva por tour · Admin Mostrando México" },
      { name: "description", content: "Configura dónde se reserva cada tour: checkout propio o GetYourGuide/Civitatis." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const SUPPLIERS = ["Mostrando México", "GetYourGuide", "Civitatis", "LadyGuía", "Viator", "Airbnb Experiences"];

interface FormState {
  tourKey: string;
  tourSlug: string;
  tourId: string;
  tourTitle: string;
  supplier: string;
  externalProductId: string;
  externalBookingUrl: string;
  bookingType: BookingType;
  active: boolean;
  notes: string;
}

const emptyForm: FormState = {
  tourKey: "",
  tourSlug: "",
  tourId: "",
  tourTitle: "",
  supplier: "Mostrando México",
  externalProductId: "",
  externalBookingUrl: "",
  bookingType: "own",
  active: true,
  notes: "",
};

function TourBookingLinksPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [filter, setFilter] = useState("");

  const catalog = useQuery(unifiedCatalogQuery("es"));
  const linksQuery = useQuery({
    queryKey: ["admin", "tour-booking-links"] as const,
    queryFn: () => listBookingLinksAdmin(),
  });

  const links = linksQuery.data?.links ?? [];
  const byKey = useMemo(() => {
    const m = new Map<string, TourBookingLink>();
    for (const l of links) {
      if (l.tourSlug) m.set(l.tourSlug, l);
      m.set(l.tourKey, l);
    }
    return m;
  }, [links]);

  const tours = useMemo(() => {
    const list = (catalog.data?.tours ?? []).map((t: any) => ({
      id: t.id as string,
      slug: (t.source === "custom" ? t.slug : makeTourSlug(t.title, t.id)) as string,
      title: t.title as string,
      location: (t.city ?? t.location ?? "") as string,
    }));
    const q = filter.trim().toLowerCase();
    return q
      ? list.filter((t) => `${t.title} ${t.location} ${t.slug}`.toLowerCase().includes(q))
      : list;
  }, [catalog.data, filter]);

  const save = useMutation({
    mutationFn: () =>
      upsertBookingLink({
        data: {
          tourKey: form.tourKey.trim(),
          tourSlug: form.tourSlug.trim() || null,
          tourId: form.tourId.trim() || null,
          tourTitle: form.tourTitle.trim() || null,
          supplier: form.supplier.trim(),
          externalProductId: form.externalProductId.trim() || null,
          externalBookingUrl: form.externalBookingUrl.trim() || null,
          bookingType: form.bookingType,
          active: form.active,
          notes: form.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success("Enlace de reserva guardado.");
      void qc.invalidateQueries({ queryKey: ["admin", "tour-booking-links"] });
      void qc.invalidateQueries({ queryKey: ["tour-booking-links", "active"] });
      setForm(emptyForm);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (tourKey: string) => deleteBookingLink({ data: { tourKey } }),
    onSuccess: () => {
      toast.success("Enlace eliminado.");
      void qc.invalidateQueries({ queryKey: ["admin", "tour-booking-links"] });
      void qc.invalidateQueries({ queryKey: ["tour-booking-links", "active"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const test = useMutation({
    mutationFn: (url: string) => testBookingUrl({ data: { url } }),
    onSuccess: (r) =>
      r.ok
        ? toast.success(`URL válida (HTTP ${r.status}).`)
        : toast.error(r.error ?? "La URL no respondió correctamente."),
    onError: (e: Error) => toast.error(e.message),
  });

  const loadTour = (t: { id: string; slug: string; title: string }) => {
    const existing = byKey.get(t.slug);
    setForm(
      existing
        ? {
            tourKey: existing.tourKey,
            tourSlug: existing.tourSlug ?? t.slug,
            tourId: existing.tourId ?? t.id,
            tourTitle: existing.tourTitle ?? t.title,
            supplier: existing.supplier,
            externalProductId: existing.externalProductId ?? "",
            externalBookingUrl: existing.externalBookingUrl ?? "",
            bookingType: existing.bookingType,
            active: existing.active,
            notes: existing.notes ?? "",
          }
        : { ...emptyForm, tourKey: t.slug, tourSlug: t.slug, tourId: t.id, tourTitle: t.title },
    );
  };

  const urlInvalid = form.bookingType === "external" && !isSafeExternalUrl(form.externalBookingUrl);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
      <AdminNav />
      <header>
        <h1 className="text-2xl font-bold">Enlaces de reserva por tour</h1>
        <p className="text-sm text-muted-foreground">
          Maya solo usa estas URLs reales. Si un tour es externo, el botón «Reservar» lleva a
          GetYourGuide o Civitatis; si es propio, al checkout de Mostrando México.
        </p>
      </header>

      <section className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="space-y-3 rounded-xl border bg-card p-4">
          <h2 className="font-semibold">1 · Elige un tour del catálogo</h2>
          <Input
            placeholder="Buscar por nombre, ciudad o slug…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="max-h-[420px] space-y-1 overflow-y-auto">
            {catalog.isLoading && <p className="text-sm text-muted-foreground">Cargando catálogo…</p>}
            {tours.slice(0, 200).map((t) => {
              const link = byKey.get(t.slug);
              return (
                <button
                  key={t.id}
                  onClick={() => loadTour(t)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{t.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">{t.slug}</span>
                  </span>
                  <span className="shrink-0 text-xs font-semibold">
                    {link
                      ? link.bookingType === "external"
                        ? `${link.supplier}${link.active ? "" : " (inactivo)"}`
                        : "Propio"
                      : "—"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-3 rounded-xl border bg-card p-4">
          <h2 className="font-semibold">2 · Configura la reserva</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Tour (clave)</Label>
              <Input value={form.tourKey} onChange={(e) => setForm({ ...form, tourKey: e.target.value })} placeholder="slug del tour" />
            </div>
            <div>
              <Label>Plataforma</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              >
                {SUPPLIERS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tipo de reserva</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.bookingType}
                onChange={(e) => setForm({ ...form, bookingType: e.target.value as BookingType })}
              >
                <option value="own">Propio (checkout Mostrando México)</option>
                <option value="external">Externo (URL del proveedor)</option>
              </select>
            </div>
            <div>
              <Label>ID externo</Label>
              <Input
                value={form.externalProductId}
                onChange={(e) => setForm({ ...form, externalProductId: e.target.value })}
                placeholder="ej. 123456"
              />
            </div>
            <div>
              <Label>Estado</Label>
              <div className="flex h-10 items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <span className="text-sm">{form.active ? "Activo" : "Inactivo"}</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>URL de reserva (https)</Label>
              <div className="flex gap-2">
                <Input
                  value={form.externalBookingUrl}
                  onChange={(e) => setForm({ ...form, externalBookingUrl: e.target.value })}
                  placeholder="https://www.getyourguide.com/…?partner_id=…"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!isSafeExternalUrl(form.externalBookingUrl) || test.isPending}
                  onClick={() => test.mutate(form.externalBookingUrl.trim())}
                >
                  {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}
                  <span className="ml-1 hidden sm:inline">Probar</span>
                </Button>
              </div>
              {urlInvalid && (
                <p className="mt-1 text-xs text-destructive">Un tour externo necesita una URL https válida.</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Se conservan íntegros los parámetros de tracking / afiliación de la URL.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>Notas internas</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button disabled={!form.tourKey.trim() || urlInvalid || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Guardar
            </Button>
            <Button variant="ghost" onClick={() => setForm(emptyForm)}>Limpiar</Button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-3 font-semibold">
          Enlaces configurados ({links.length}) · Vinculados{" "}
          {links.filter((l) => l.tourSlug).length} · Pendientes{" "}
          {links.filter((l) => !l.tourSlug).length}
        </h2>
        {linksQuery.isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2">Tour</th>
                <th>Proveedor</th>
                <th>External ID</th>
                <th>URL</th>
                <th>Tipo</th>
                <th>Activo</th>
                <th>Vinculación</th>
                <th>Actualizado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.tourKey} className="border-t align-top">
                  <td className="py-2">
                    <span className="block font-medium">{l.tourTitle ?? l.tourKey}</span>
                    <span className="text-xs text-muted-foreground">{l.tourSlug ?? l.tourKey}</span>
                  </td>
                  <td>{l.supplier}</td>
                  <td className="font-mono text-xs">{l.externalProductId ?? "—"}</td>
                  <td className="max-w-[240px] truncate">
                    {l.externalBookingUrl ? (
                      <a
                        href={l.externalBookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> abrir
                      </a>
                    ) : (
                      <span className="text-muted-foreground">checkout propio</span>
                    )}
                  </td>
                  <td>{l.bookingType === "external" ? "Externo" : "Propio"}</td>
                  <td>{l.active ? "Sí" : "No"}</td>
                  <td>
                    {l.tourSlug ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        VINCULADO
                      </span>
                    ) : (
                      <span
                        className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive"
                        title={l.notes ?? undefined}
                      >
                        PENDIENTE DE VINCULAR
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-xs text-muted-foreground">
                    {l.updatedAt ? new Date(l.updatedAt).toLocaleString("es-MX") : "—"}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {l.externalBookingUrl && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={test.isPending}
                        onClick={() => test.mutate(l.externalBookingUrl as string)}
                        title="Probar URL"
                      >
                        <TestTube2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setForm({
                          tourKey: l.tourKey,
                          tourSlug: l.tourSlug ?? "",
                          tourId: l.tourId ?? "",
                          tourTitle: l.tourTitle ?? "",
                          supplier: l.supplier,
                          externalProductId: l.externalProductId ?? "",
                          externalBookingUrl: l.externalBookingUrl ?? "",
                          bookingType: l.bookingType,
                          active: l.active,
                          notes: l.notes ?? "",
                        })
                      }
                    >
                      Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove.mutate(l.tourKey)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}
